import './style.scss';

import { Input, message } from 'antd';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackButtonClick } from '../../analytics/mixpanel';
import CollapseSidePanelIcon from '../../assets/CollapseSidePanelIcon';
import SearchOutlined from '../../assets/common/SearchOutlined';
import FileCompareOutlined from '../../assets/doccompare/FileCompareOutlined';
import { ROOT_URL } from '../../config';
import { acceptTypes } from '../../Constants';
import {
  contextComparison,
  deleteComparison,
  getSavedComparisons,
  handleExport,
} from '../../Redux/FileCompare/fileCompareActions';
import { setDocCompareSidebar, sidebarStateSelector } from '../../Redux/sidebar/sidebarReducer';
import { getValidAccessToken } from '../../utils/authUtils';
import { formatIndianLongDate, getAxiosErrorMessage, getSearchRegex } from '../../utils/functions';
import SavedComparisonSkeleton from '../SavedComparisons/SavedComparisonSkeleton';
import Button from '../UI/Button';
import ConfirmationModal from '../UI/ConfirmationModal';
import UpdateTextModal from '../UpdateTextModal';
import SavedComparisonsSidebarItem from './SavedComparisonsSidebarItem';

const initialModalData = {
  edit: false,
  delete: false,
  export: false,
  data: null,
  loader: null,
};

const SavedComparisonsSidebar = ({ savedComparisons, dataSourceHandler, from }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getSavedCompareStatus } = useSelector((state) => state.compareData);
  const { docCompareSidebarVisible } = useSelector(sidebarStateSelector);
  const [filterSavedComparisons, setFilterSavedComparisons] = useState(null);
  const [searchParams] = useSearchParams();
  const uuid = Number(searchParams.get('uuid'));
  const searchRef = useRef(null);
  const fileRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState(initialModalData);

  useEffect(() => {
    if (savedComparisons) {
      if (searchRef.current) {
        const regex = getSearchRegex(searchRef.current);
        const result = savedComparisons?.filter(({ title }) => {
          if (title === null) {
            return false;
          }
          return title?.match(regex);
        });
        setFilterSavedComparisons(result);
      } else if (Array.isArray(savedComparisons)) {
        setFilterSavedComparisons(savedComparisons);
      }
      if (uuid) {
        setTimeout(() => {
          const element = document.getElementById(uuid);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [savedComparisons]);

  const toggleSideBar = () => {
    dispatch(setDocCompareSidebar(!docCompareSidebarVisible));
  };

  const onClick = () => {
    if (!docCompareSidebarVisible) {
      dispatch(setDocCompareSidebar(true));
    }
  };

  const debouncedHandleSearch = debounce((searchValue) => {
    const regex = getSearchRegex(searchValue);
    const result = savedComparisons?.filter(({ title }) => {
      if (title === null) {
        return false;
      }
      return typeof title === 'number' ? title?.toString()?.match(regex) : title?.match(regex);
    });
    setFilterSavedComparisons(result);
  }, 300);

  const handleSearch = (e) => {
    const value = e.target.value?.trim();
    searchRef.current = value;
    debouncedHandleSearch(value);
  };

  const onComparisonClick = (data) => {
    navigate(`/viewer?uuid=${data?.id}&type=SAVED`);
    if (dataSourceHandler) {
      dataSourceHandler();
    }
  };

  const sendData = async (filesData) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      // formData.append('files', filesData);
      filesData.forEach((file) => {
        formData.append('files', file);
      });
      // documentsFormData.append('modified_document', uploadData.modifiedFile);
      const response = await contextComparison(formData);
      if (response.statusText === 'Created' && response.data?.id) {
        await dispatch(getSavedComparisons());
        navigate(`/viewer?uuid=${response.data.id}&type=SAVED`);
      }
    } catch (error) {
      message.error(getAxiosErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length <= 1) {
      message.error('Please select minimum 2 files');
      return;
    }
    if (files.length > 6) {
      message.error('You cannot select more than 6 files');
      return;
    }
    sendData(files);
  };

  const handleModal = (name, data) => {
    if (name === 'export') {
      dispatch(handleExport(data?.id));
    } else {
      setModalData((prev) => ({ ...prev, [name]: true, data }));
    }
  };

  const closeModal = () => {
    setModalData({ ...initialModalData });
  };

  const handleDelete = async () => {
    setModalData((prev) => ({ ...prev, loader: 'DELETE' }));
    trackButtonClick({
      type: 'Delete previously compared document',
    });
    try {
      await dispatch(deleteComparison(modalData.data?.id));
      closeModal();
      navigate('/doccompare');
      await dispatch(getSavedComparisons());
    } finally {
      if (modalData.loader) {
        closeModal();
      }
    }
  };

  const handleUpdate = async (value) => {
    setModalData((prev) => ({ ...prev, loader: 'UPDATE' }));
    trackButtonClick({
      type: 'Delete previously compared document',
    });
    try {
      await axios.patch(
        `${ROOT_URL}/context_comparison/${modalData.data?.id}/`,
        {
          operation: 'rename_title',
          comparison_title: value,
        },
        {
          headers: {
            Authorization: `Bearer ${await getValidAccessToken()}`,
          },
        }
      );
      closeModal();
      await dispatch(getSavedComparisons());
    } finally {
      if (modalData.loader) {
        closeModal();
      }
    }
  };

  const renderHeader = () => {
    return <div className='mb-16 fs-18 fw-500'>Rename comparison</div>;
  };

  return (
    <aside
      className={`saved-comparisons-aside ${docCompareSidebarVisible ? '' : 'collapse-active'}`}
    >
      <div className='saved-data-search-container' role='button' onClick={onClick}>
        <Input
          prefix={<SearchOutlined />}
          placeholder='Search Files'
          style={{ height: '40px' }}
          onChange={handleSearch}
          allowClear
        />
      </div>
      <div className='aside-collapse-btn' onClick={toggleSideBar}>
        <CollapseSidePanelIcon />
      </div>
      {from !== 'dashboard' && (
        <div className='upload-doc-btn-container mb-16'>
          <input
            type='file'
            name='file'
            onChange={onChange}
            hidden
            ref={fileRef}
            multiple
            accept={acceptTypes}
          />
          <Button
            type='primary'
            style={{ height: '42px', width: '100%' }}
            onClick={() => {
              fileRef.current.click();
            }}
            icon={<FileCompareOutlined />}
            loading={isLoading}
          >
            {docCompareSidebarVisible ? 'New Comparison' : ''}
          </Button>
        </div>
      )}
      {getSavedCompareStatus === 'LOADING' ? (
        <SavedComparisonSkeleton />
      ) : (
        <ul className={`saved-comparisons-ul scrollbar ${from === 'dashboard' ? 'expand' : ''}`}>
          {filterSavedComparisons &&
            filterSavedComparisons?.map((data) => (
              <SavedComparisonsSidebarItem
                key={data?.id}
                data={data}
                uuid={uuid}
                onComparisonClick={onComparisonClick}
                formatIndianLongDate={formatIndianLongDate}
                handleModal={handleModal}
              />
            ))}
        </ul>
      )}
      <UpdateTextModal
        isOpen={modalData.edit}
        initialText={modalData.data?.title}
        onClose={closeModal}
        onSave={handleUpdate}
        loading={modalData.loader === 'UPDATE'}
        renderHeader={renderHeader}
      />
      <ConfirmationModal
        isOpen={modalData.delete}
        text={`Are you sure you want to delete this "${modalData.data?.title}" comparison??`}
        onCancel={closeModal}
        onSubmit={handleDelete}
        loading={modalData.loader === 'DELETE'}
        submitButtonProps={{ danger: true }}
      />
    </aside>
  );
};

export default SavedComparisonsSidebar;
